import { CurrencyPipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';

import { IbanFormatterPipe } from 'angular-iban';
import { combineLatest, EMPTY, merge, Observable, of, Subject, throwError } from 'rxjs';
import { catchError, debounceTime, filter, finalize, map, shareReplay, switchMap, takeUntil, tap } from 'rxjs/operators';

import { BankServiceConnection, CreateOutgoingPaymentRequest, Error as APIError, FinancingHold, Invoice, OutgoingPayment, OutgoingPaymentReason, Permission, VopMode } from 'src/app/api/models';
import { BankServiceConnectionsService, OutgoingPaymentsService } from 'src/app/api/services';
import {
  ConfirmationDialogComponent,
  ConfirmationDialogDetail,
  ConfirmationDialogModel
} from '../../components/confirmation-dialog/confirmation-dialog.component';
import { getStatusClass } from '../../components/outgoing-payment-status-label/outgoing-payment-status-label.component';
import { ModelMap, PaymentTransferStatusesMapCache, UsersMapCache } from '../../model/model-map-cache';
import { PaymentTransferStatus } from '../../model/payment-transfer-status';
import { User } from '../../model/user';
import { InvoicePeerService } from '../../service/invoice-peer.service';
import { RealtimeNotificationsService } from '../../service/realtime-notifications.service';
import { UserService } from '../../service/user.service';
import { ConfirmationService } from '../../shared/confirmation/confirmation.service';
import { describeFinancingHold, financingOnHoldRefusal, refusalMessage } from '../../shared/financing-holds/financing-hold-text';
import {
  PayeeVerificationState,
  VOP_IDLE,
  verifyPayee$,
  vopBlocksPayout,
  VopOverrideChoice,
  vopOverrideNeedsReason,
  vopOverrideOffered,
  vopOverrideRequest,
} from '../../shared/vop/payee-verification';
import { vopHolderName, vopResultLabel } from '../../shared/vop/vop-result';
import { ValidateIban } from '../../validators/iban.validator';

// The payment reasons a financing hold stops: codes L and T
// (add-financing-holds, design D5).
const HELD_PAYMENT_REASONS = ['INVOICE_FINANCING', 'FINAL_PAYMENT'];

const PAYMENT_NOT_CREATED = 'The payment was not created';

// The payment reasons whose recipient is the proposed one, filled in by
// the widget; for any other (commission P, other O) the handler types it.
const PROPOSED_RECIPIENT_REASONS = ['INVOICE_FINANCING', 'FINAL_PAYMENT'];

// How long a typed recipient's IBAN and name must stay unchanged before
// the widget asks for their VoP result, so it never asks about a
// half-typed pair.
export const TYPED_PAYEE_DEBOUNCE_MS = 800;

// The IBAN and name a payout's Verification of Payee is asked about.
interface Payee {
  iban: string;
  name: string;
}

// The pair as VoP is asked about it, or null while either is missing.
function payeeOf(iban: string | null, name: string | null): Payee | null {
  const compactIban = (iban ?? '').replace(/\s/g, '');
  const trimmedName = (name ?? '').trim();
  return compactIban && trimmedName ? { iban: compactIban, name: trimmedName } : null;
}

function samePayee(a: Payee | null, b: Payee | null): boolean {
  return a?.iban === b?.iban && a?.name === b?.name;
}

// The lookups the payment form and its list read.
interface OutgoingPaymentLookups {
  statuses: ModelMap<PaymentTransferStatus>;
  users: ModelMap<User>;
  bankServiceConnections: BankServiceConnection[];
  outgoingPaymentReasons: OutgoingPaymentReason[];
}

@Component({
  selector: 'app-invoice-outgoing-payment-widget',
  templateUrl: './invoice-outgoing-payment-widget.component.html',
  styleUrls: ['./invoice-outgoing-payment-widget.component.scss'],
  providers: [CurrencyPipe]
})
export class InvoiceOutgoingPaymentWidgetComponent implements OnInit, OnDestroy {
  private readonly ngDestroy$ = new Subject<void>();

  invoice$: Observable<Invoice>;
  outgoingPayments$: Observable<OutgoingPayment[]>;
  public lookups$: Observable<OutgoingPaymentLookups>;
  // Gate the payment actions on the permissions the backend enforces, not on a
  // role string — the granting Entra role value is migrating from
  // `payments.create` to `Payer` and both grant the same permission set.
  // Each per-payment action has its own permission server-side, so each gets
  // its own gate rather than sharing one blanket "can touch payments" check.
  canCreatePayment$: Observable<boolean>;
  canCancelPayment$: Observable<boolean>;
  canRetryPayment$: Observable<boolean>;
  canResubmitPayment$: Observable<boolean>;

  private lookups: OutgoingPaymentLookups;
  invoice: Invoice;
  public selectedItem: OutgoingPayment | null;

  form: UntypedFormGroup;
  locked = false;
  added = false;
  saving = false;
  public toIbanStatus: 'verified' | 'unverified' | 'unknown' | null = null;
  public getStatusClass = getStatusClass;
  public showAdvancedOptions = false;
  public invoiceIbanMismatch = false;
  public noVerifiedAccount = false;
  public unknownLEService = false;
  // A 422 financing_on_hold from creating, retrying or resubmitting a
  // payment, shown with the holds it lists until the next action.
  public holdRefusal: { lead: string; holds: FinancingHold[] } | null = null;
  // Any other structured 422 from creating, retrying or resubmitting a
  // payment (express_mismatch, iban_mismatch, no_verified_bank_account, ...),
  // shown in the server's words until the next action. amount_mismatch, which
  // only the create answers, has its dialog instead.
  public refusal: { lead: string; message: string } | null = null;
  public readonly describeHold = describeFinancingHold;
  // The proposed recipient's Verification of Payee (issue #360): asked about
  // when a financing, final payment or invoicing-service payout opens.
  public vop: PayeeVerificationState = VOP_IDLE;
  public readonly vopResultLabel = vopResultLabel;
  public readonly vopHolderName = vopHolderName;
  private readonly payee$ = new Subject<Payee | null>();
  private payee: Payee | null = null;
  // The environment's VoP mode, read once when the widget opens (issue
  // #682). A failed read is made again when a payee is next asked about.
  private vopMode$: Observable<VopMode>;

  constructor(
    private invoicePeerService: InvoicePeerService,
    private outgoingPaymentsService: OutgoingPaymentsService,
    private bankServiceConnectionsService: BankServiceConnectionsService,
    private usersMapCache: UsersMapCache,
    private paymentTransferStatusesMapCache: PaymentTransferStatusesMapCache,
    private realtime: RealtimeNotificationsService,
    private builder: UntypedFormBuilder,
    private dialog: MatDialog,
    private confirmationService: ConfirmationService,
    private userService: UserService,
    private currencyPipe: CurrencyPipe) {
    this.form = this.builder.group({
      amount: [null, [Validators.required, Validators.min(0.01)]],
      referenceText: [null],
      referenceNumber: [null],
      reasonId: [null, Validators.required],
      channelId: [null, Validators.required],
      toBankAccount: [null, [Validators.required, ValidateIban]],
      toBic: [null],
      // AI: Added recipient details
      to_name: [null, Validators.required],
      to_country: [null, Validators.required],
      to_street_address: [null],
      to_city: [null],
      to_postal_code: [null],
      to_business_id: [null],
      is_express: [false, Validators.required],
      sequence: [1, [Validators.required, Validators.min(1)]],
      // The VoP override, offered in enforce for a result other than a match.
      vop_override: [false],
      vop_override_reason: [null],
    });
  }

  // While a hold applies, a financing or final payment is refused, so the
  // form shows the holds and keeps its submit disabled. The server remains
  // the authority: it refuses whatever this misses.
  get financingHoldBlocksPayment(): boolean {
    return !!this.invoice?.financing_on_hold && HELD_PAYMENT_REASONS.includes(this.form.get('reasonId').value);
  }

  // The handler's VoP override as the form holds it.
  private get vopOverrideChoice(): VopOverrideChoice {
    return { made: !!this.form.get('vop_override').value, reason: this.form.get('vop_override_reason').value };
  }

  public get vopOverrideOffered(): boolean {
    return vopOverrideOffered(this.vop);
  }

  public get vopOverrideNeedsReason(): boolean {
    return vopOverrideNeedsReason(this.vop);
  }

  // In enforce, while VoP waits and while the server would refuse the
  // payout for VoP, Transfer stays disabled; in observe never. The server
  // remains the authority.
  public get vopBlocksPayment(): boolean {
    return vopBlocksPayout(this.vop, this.vopOverrideChoice);
  }

  // True while VoP waits in observe, the one mode where the handler may
  // transfer without waiting.
  public get vopWaitIsOptional(): boolean {
    return this.vop.kind === 'waiting' && this.vop.mode === VopMode.Observe;
  }

  /**
   * Asks for the VoP result of the payout's recipient, or forgets it with
   * a null IBAN or name. A financing, final payment or invoicing-service
   * payout asks when its reason is chosen; a commission or other payout
   * asks about its typed recipient once the IBAN and name have stayed
   * unchanged for TYPED_PAYEE_DEBOUNCE_MS (issue #413).
   */
  public checkPayee(iban: string | null, name: string | null): void {
    this.payee = payeeOf(iban, name);
    this.payee$.next(this.payee);
  }

  // True while the form pays a recipient the handler types.
  private get recipientTyped(): boolean {
    const reasonKey = this.form.get('reasonId').value;
    return this.added && !!reasonKey && !PROPOSED_RECIPIENT_REASONS.includes(reasonKey);
  }

  // The typed recipient as the form holds it, or null while the IBAN is
  // missing or not yet a valid IBAN, or the name is missing.
  private typedPayee(): Payee | null {
    const ibanControl = this.form.get('toBankAccount');
    if (!ibanControl.valid) {
      return null;
    }
    return payeeOf(ibanControl.value, this.form.get('to_name').value);
  }

  // Asks about the same recipient again: "Check again", and after the
  // server refuses a payout for VoP.
  public checkPayeeAgain(): void {
    if (this.payee) {
      this.payee$.next(this.payee);
    }
  }

  add(): void {
    this.invoicePeerService.lock();
    this.added = true;
    this.clearRefusals();
    this.checkPayee(null, null);
    this.form.reset({
      amount: this.invoice.proposed_outgoing_payment?.amount ? parseFloat(this.invoice.proposed_outgoing_payment.amount.amount) : null,
      // AI: Reference number is now available for all payments.
      referenceNumber: this.invoice.proposed_outgoing_payment?.reference_number ?? null,
      toBankAccount: this.invoice.iban,
      is_express: false,
      sequence: 1
    });
    this.showAdvancedOptions = false;
  }

  revert(): void {
    this.invoicePeerService.unlock();
    this.added = false;
    this.clearRefusals();
    this.checkPayee(null, null);
  }

  save(): void {
    if (this.financingHoldBlocksPayment || this.vopBlocksPayment) {
      return;
    }
    // AI: Use getRawValue to include disabled form fields.
    const formValue = this.form.getRawValue();
    const selectedChannel = this.lookups.bankServiceConnections.find(c => c.provider_key === formValue.channelId);

    // Recipient IBAN is piped through the angular-iban formatter so it reads
    // grouped (per the console IBAN-display convention) inside the dialog.
    const formattedIban = new IbanFormatterPipe().transform(formValue.toBankAccount);
    const toParts = [formValue.to_name, formValue.toBic, formattedIban].filter(Boolean);

    // WHO → WHAT → WHEN: recipient first, then amount, then the
    // channel/reference extras (matches the send-invoice/send-offer confirms).
    const details: ConfirmationDialogDetail[] = [
      { label: 'To:', value: toParts.join('\n') },
      { label: 'Amount:', value: this.currencyPipe.transform(formValue.amount, 'EUR') ?? `${formValue.amount} EUR` },
      { label: 'Channel:', value: selectedChannel?.display_name },
      { label: 'Reference:', value: formValue.referenceText },
      { label: 'Reference number:', value: formValue.referenceNumber || '(Not sent)' },
    ];

    if (formValue.is_express) {
      details.push({ label: 'Payment type:', value: 'Express' });
    }

    const vopOverride = vopOverrideRequest(this.vop, this.vopOverrideChoice);
    if (this.vop.kind === 'result') {
      const holder = vopHolderName(this.vop.result);
      details.push({
        label: 'Verification of Payee:',
        value: vopResultLabel(this.vop.result) + (holder ? ` (holder: ${holder})` : '') + (vopOverride ? ', VoP override' : ''),
      });
    }

    const dialogData = new ConfirmationDialogModel(
      'Confirm Payment Transfer',
      'Please confirm the following payment transfer:',
      'Transfer',
      'Cancel',
      details
    );

    this.confirmationService.confirm(dialogData)
      .pipe(filter(result => !!result))
      .subscribe(() => {
        const request: CreateOutgoingPaymentRequest = {
          provider_key: formValue.channelId,
          reason_key: formValue.reasonId,
          amount: {
            amount: formValue.amount.toString(),
            currency: 'EUR'
          },
          // AI: Strip whitespace from IBAN.
          to_iban: formValue.toBankAccount.replace(/\s/g, ''),
          to_bic: formValue.toBic,
          to_name: formValue.to_name,
          to_country: formValue.to_country,
          to_street_address: formValue.to_street_address,
          to_city: formValue.to_city,
          to_postal_code: formValue.to_postal_code,
          to_business_id: formValue.to_business_id,
          reference_text: formValue.referenceText,
          reference_number: formValue.referenceNumber,
          is_express: formValue.is_express,
          sequence: formValue.sequence,
        };
        if (vopOverride) {
          request.vop_override = vopOverride;
        }
        this.createPayment(request);
      });
  }

  private createPayment(request: CreateOutgoingPaymentRequest): void {
    this.saving = true;
    this.clearRefusals();
    this.outgoingPaymentsService.createOutgoingPayment({ id: this.invoice.id, body: request })
      .pipe(
        catchError((err: HttpErrorResponse) => {
          if (err.error?.error_code === 'amount_mismatch') {
            this.saving = false;
            this.showAmountMismatchDialog(err.error.details, request);
            return EMPTY;
          }
          throw err;
        }),
        catchError((err: unknown) => {
          const refused = this.showFinancingHoldRefusal(err, PAYMENT_NOT_CREATED);
          this.saving = false;
          return refused;
        }),
        // A VoP refusal means the widget's result is stale: ask again, and
        // show the refusal as any other.
        catchError((err: unknown) => {
          const code = (err as { error?: { error_code?: unknown } } | null)?.error?.error_code;
          if (typeof code === 'string' && code.startsWith('vop_')) {
            this.checkPayeeAgain();
          }
          return throwError(err);
        }),
        catchError((err: unknown) => this.showStructuredRefusal(err, PAYMENT_NOT_CREATED)),
        // HttpErrorInterceptor completes a 401, a 403 or an aborted request
        // without a value or an error, which would leave saving set. It does
        // not undo the amount_mismatch override's saving: that request starts
        // only once MatDialog.afterClosed emits, asynchronously, after this
        // request is over and finalize has run.
        finalize(() => this.saving = false)
      )
      .subscribe({
        next: () => this.onSaveSuccess(),
        error: () => this.saving = false,
      });
  }

  private showAmountMismatchDialog(details: APIError['details'], originalRequest: CreateOutgoingPaymentRequest): void {
    const expectedAmount = details?.expected_amount?.amount ?? '?';
    const actualAmount = details?.actual_amount?.amount ?? '?';
    const dialogData = new ConfirmationDialogModel(
      'Amount Mismatch',
      `The payment amount (${actualAmount} EUR) does not match the calculated amount (${expectedAmount} EUR). Continue anyway?`,
      'Continue with entered amount',
      'Cancel'
    );
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      maxWidth: '400px',
      data: dialogData
    });
    dialogRef.afterClosed()
      .pipe(filter(result => !!result))
      .subscribe(() => {
        const overrideRequest = { ...originalRequest, allow_amount_override: true };
        this.createPayment(overrideRequest);
      });
  }

  private onSaveSuccess(): void {
    this.invoicePeerService.unlock();
    this.selectedItem = null;
    this.saving = false;
    this.added = false;
    this.outgoingPayments$ = this.getOutgoingPayments(this.invoice.id);
  }

  public itemClicked(item: OutgoingPayment) {
    this.selectedItem = (item !== this.selectedItem ? item : null);
  }

  ngOnInit() {

    this.canCreatePayment$ = this.userService.hasPermission(Permission.PaymentsCreate);
    this.canCancelPayment$ = this.userService.hasPermission(Permission.PaymentsCancel);
    this.canRetryPayment$ = this.userService.hasPermission(Permission.PaymentsRetry);
    this.canResubmitPayment$ = this.userService.hasPermission(Permission.PaymentsResubmit);

    this.lookups$ = combineLatest([
      this.paymentTransferStatusesMapCache.map$,
      this.usersMapCache.map$,
      this.bankServiceConnectionsService.listBankServiceConnections(),
      this.outgoingPaymentsService.listOutgoingPaymentReasons()
    ]).pipe(
      map(([statuses, users, bankServiceConnections, outgoingPaymentReasons]) => ({
        statuses,
        users,
        bankServiceConnections,
        outgoingPaymentReasons
      })),
      tap(lookups => this.lookups = lookups)
    );

    // Read up front, so that the mode is known by the time a payout opens;
    // in off the widget then asks about no payee.
    this.vopMode$ = this.outgoingPaymentsService.getVopMode().pipe(
      map((setting) => setting.mode),
      shareReplay(1),
    );
    this.vopMode$.pipe(takeUntil(this.ngDestroy$)).subscribe({ error: () => undefined });

    this.payee$
      .pipe(
        switchMap((payee) => payee
          ? verifyPayee$(this.vopMode$,
            () => this.outgoingPaymentsService.verifyPayee({ body: { iban: payee.iban, name: payee.name } }))
          : of(VOP_IDLE)),
        takeUntil(this.ngDestroy$),
      )
      .subscribe((state) => {
        this.vop = state;
        this.form.patchValue({ vop_override: false, vop_override_reason: null }, { emitEvent: false });
      });

    // A typed recipient: a change of the IBAN or name forgets the result
    // at once, so a stale one is never shown or paid on, and the new pair
    // is asked about once it has stayed unchanged for the debounce time.
    const typedPayee$ = merge(
      this.form.get('toBankAccount').valueChanges,
      this.form.get('to_name').valueChanges,
    ).pipe(
      filter(() => this.recipientTyped),
      map(() => this.typedPayee()),
    );
    typedPayee$
      .pipe(takeUntil(this.ngDestroy$))
      .subscribe((payee) => {
        if (this.payee && !samePayee(this.payee, payee)) {
          this.checkPayee(null, null);
        }
      });
    typedPayee$
      .pipe(
        filter((payee) => !!payee),
        debounceTime(TYPED_PAYEE_DEBOUNCE_MS),
        takeUntil(this.ngDestroy$),
      )
      .subscribe(() => {
        const payee = this.recipientTyped ? this.typedPayee() : null;
        if (payee && !samePayee(this.payee, payee)) {
          this.checkPayee(payee.iban, payee.name);
        }
      });

    this.invoice$ = this.invoicePeerService
      .getInvoice()
      .pipe(
        filter(invoice => !!invoice),
        tap(invoice => {
          this.invoice = invoice;
          this.outgoingPayments$ = this.getOutgoingPayments(invoice.id);
        }
        ));

    this.realtime.events$
      .pipe(takeUntil(this.ngDestroy$))
      .subscribe((event) => {
        if (
          (event.event_type === 'payment_created' || event.event_type === 'payment_status_updated') &&
          this.invoice
        ) {
          this.outgoingPayments$ = this.getOutgoingPayments(this.invoice.id);
        }
      });

    this.invoicePeerService.getState().subscribe((state) => {
      if (state === 'locked') {
        this.locked = true;
      } else if (state === 'unlocked') {
        this.locked = false;
      }
    });

    this.form.get('reasonId').valueChanges
      .pipe(takeUntil(this.ngDestroy$))
      .subscribe((reasonKey: string) => {
        const isExpressControl = this.form.get('is_express');
        if (reasonKey === 'INVOICE_FINANCING') {
          isExpressControl.setValue(this.invoice.proposed_outgoing_payment?.is_express ?? false);
        } else {
          isExpressControl.setValue(false);
        }

        // AI: Handle recipient details based on payment reason.
        const recipientControls = {
          to_name: this.form.get('to_name'),
          to_country: this.form.get('to_country'),
          to_street_address: this.form.get('to_street_address'),
          to_city: this.form.get('to_city'),
          to_postal_code: this.form.get('to_postal_code'),
          to_business_id: this.form.get('to_business_id'),
        };

        if (reasonKey === 'INVOICE_FINANCING' || reasonKey === 'FINAL_PAYMENT') {
          // AI: Default to client details for financing and final payments.
          this.form.patchValue({
            to_name: this.invoice.client.name,
            to_country: this.invoice.client.country,
            to_street_address: null,
            to_city: null,
            to_postal_code: null,
            to_business_id: this.invoice.client.business_id
          });

          // AI: Set reference text for financing and final payment.
          this.form.get('referenceText').setValue(this.invoice.proposed_outgoing_payment?.reference_text ?? null);

          Object.values(recipientControls).forEach(control => {
            control.disable();
            control.clearValidators();
            control.updateValueAndValidity();
          });

          // Pre-fill and lock IBAN/BIC from the ownership-confirmed bank account.
          this.invoiceIbanMismatch = false;
          this.noVerifiedAccount = false;
          this.unknownLEService = false;

          // The client's effective Payouts to the invoicing service (issue #470)
          // chooses the payee, never its client type. A client without
          // preferences, which the server always sends, gets neither.
          const payoutsToInvoicingService = this.invoice.client?.preferences?.payouts_to_invoicing_service?.value;
          if (payoutsToInvoicingService === false) {
            const verifiedAccount = this.invoice.client?.bank_accounts?.find(ba => ba.verified_at);
            if (verifiedAccount) {
              this.form.patchValue({ toBankAccount: verifiedAccount.iban, toBic: verifiedAccount.bic });
              this.form.get('toBankAccount').disable();
              this.form.get('toBic').disable();

              const invoiceIban = this.invoice.iban?.replace(/\s/g, '').toUpperCase();
              const verifiedIban = verifiedAccount.iban?.replace(/\s/g, '').toUpperCase();
              this.invoiceIbanMismatch = !!(invoiceIban && verifiedIban && invoiceIban !== verifiedIban);
            } else {
              this.noVerifiedAccount = true;
              this.form.get('toBankAccount').disable();
              this.form.get('toBic').disable();
            }
          } else if (payoutsToInvoicingService === true) {
            // The payout goes to the invoicing service, under the service's
            // own name and business id as the proposed payment gives them,
            // whatever the VoP mode (issue #683). The proposed payment has an
            // IBAN when the invoicing service is registered.
            const proposed = this.invoice.proposed_outgoing_payment;
            if (proposed?.to_iban) {
              this.form.patchValue({
                toBankAccount: proposed.to_iban,
                toBic: proposed.to_bic ?? null,
                to_name: proposed.to_name ?? null,
                to_business_id: proposed.to_business_id ?? null,
              });
              this.form.get('toBankAccount').disable();
              this.form.get('toBic').disable();
            } else {
              this.unknownLEService = true;
              this.form.get('toBankAccount').disable();
              this.form.get('toBic').disable();
            }
          }

          // The proposed recipient, when there is one to pay.
          const recipient = this.form.getRawValue();
          if (this.noVerifiedAccount || this.unknownLEService) {
            this.checkPayee(null, null);
          } else {
            this.checkPayee(recipient.toBankAccount, recipient.to_name);
          }
        } else {
          // AI: Allow user to input recipient details for other payment types.
          const resetValues = {};
          Object.keys(recipientControls).forEach(key => resetValues[key] = '');
          this.form.patchValue(resetValues);

          // AI: Clear reference text for other payment reasons.
          this.form.get('referenceText').setValue(null);

          this.form.get('toBankAccount').enable();
          this.form.get('toBic').enable();
          // A typed recipient is asked about once it is entered.
          this.checkPayee(null, null);
          this.invoiceIbanMismatch = false;
          this.noVerifiedAccount = false;
          this.unknownLEService = false;

          Object.values(recipientControls).forEach(control => control.enable());
          recipientControls.to_name.setValidators(Validators.required);
          recipientControls.to_country.setValidators(Validators.required);
          Object.values(recipientControls).forEach(control => control.updateValueAndValidity());
        }
      });

    this.form.get('toBankAccount').valueChanges
      .pipe(takeUntil(this.ngDestroy$))
      .subscribe((iban: string) => {
        if (!iban || !this.invoice?.client?.bank_accounts) {
          this.toIbanStatus = null;
          return;
        }

        const cleanIban = iban.replace(/\s/g, '').toUpperCase();
        const matchedAccount = this.invoice.client.bank_accounts.find(
          ba => ba.iban && ba.iban.replace(/\s/g, '').toUpperCase() === cleanIban
        );

        if (matchedAccount) {
          this.toIbanStatus = matchedAccount.verified_at ? 'verified' : 'unverified';
        } else {
          this.toIbanStatus = 'unknown';
        }
      });
  }

  toggleAdvancedOptions(): void {
    this.showAdvancedOptions = !this.showAdvancedOptions;
  }

  retryPayment(payment: OutgoingPayment, event: MouseEvent): void {
    event.stopPropagation();

    const dialogData = new ConfirmationDialogModel(
      'Retry Payment Submission?',
      'Are you sure you want to retry submitting this payment?',
      'Retry'
    );

    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      maxWidth: '400px',
      data: dialogData
    });

    dialogRef.afterClosed()
      .pipe(filter(result => !!result))
      .subscribe(() => {
        this.sendAgain(
          this.outgoingPaymentsService.retryOutgoingPayment({ invoice_id: this.invoice.id, payment_id: payment.id }),
          'The payment was not retried',
        );
      });
  }

  cancelPayment(payment: OutgoingPayment, event: MouseEvent): void {
    event.stopPropagation();

    const dialogData = new ConfirmationDialogModel(
      'Cancel Payment Submission?',
      'Are you sure you want to cancel this payment submission? This action cannot be undone and may not be possible if the payment has already been processed by the bank.',
      'Cancel Payment',
      'Keep Payment'
    );

    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      maxWidth: '400px',
      data: dialogData
    });

    dialogRef.afterClosed()
      .pipe(filter(result => !!result))
      .subscribe(() => {
        this.outgoingPaymentsService.cancelOutgoingPayment({ invoice_id: this.invoice.id, payment_id: payment.id }).subscribe();
      });
  }

  resubmitPayment(payment: OutgoingPayment, event: MouseEvent): void {
    event.stopPropagation();

    const dialogData = new ConfirmationDialogModel(
      'Resubmit Payment?',
      'This is intended for cases where a payment is stuck and suspected to have not been processed by the bank. The payment will be resubmitted with the same identifiers. If the original payment was processed, the bank should detect this as a duplicate, but this cannot be guaranteed. Are you sure you want to resubmit this payment?',
      'Resubmit'
    );

    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      maxWidth: '400px',
      data: dialogData
    });

    dialogRef.afterClosed()
      .pipe(filter(result => !!result))
      .subscribe(() => {
        this.sendAgain(
          this.outgoingPaymentsService.resubmitOutgoingPayment({ invoice_id: this.invoice.id, payment_id: payment.id }),
          'The payment was not resubmitted',
        );
      });
  }

  ngOnDestroy(): void {
    this.ngDestroy$.next();
    this.ngDestroy$.complete();
  }

  // A refusal is shown until the next action: starting or cancelling a
  // payment, or sending one.
  private clearRefusals(): void {
    this.holdRefusal = null;
    this.refusal = null;
  }

  // Retry and Resubmit send an existing payment to the bank again, and the
  // server refuses it with the create's structured 422s: financing_on_hold
  // is shown with its holds, any other one (express_mismatch) in the
  // server's words. Any other error already got the HTTP error
  // interceptor's dialog, so it ends here.
  private sendAgain(sent$: Observable<unknown>, lead: string): void {
    this.clearRefusals();
    sent$
      .pipe(
        catchError((err: unknown) => this.showFinancingHoldRefusal(err, lead)),
        catchError((err: unknown) => this.showStructuredRefusal(err, lead)),
      )
      .subscribe({ error: () => undefined });
  }

  // A 422 financing_on_hold is shown with the holds it lists; any other
  // error propagates as before.
  private showFinancingHoldRefusal(err: unknown, lead: string): Observable<never> {
    const holds = financingOnHoldRefusal(err);
    if (!holds) {
      return throwError(err);
    }
    this.holdRefusal = { lead, holds };
    return EMPTY;
  }

  // A structured 422, one whose body carries an `error_code`, is shown with
  // the server's message: the HTTP error interceptor opens no dialog for it
  // and leaves it to this widget, so otherwise the handler would see nothing.
  // Any other error, such as a 422 without an `error_code`, already got the
  // interceptor's dialog and propagates as before.
  private showStructuredRefusal(err: unknown, lead: string): Observable<never> {
    const code = (err as { error?: { error_code?: unknown } } | null)?.error?.error_code;
    if (!(err instanceof HttpErrorResponse) || err.status !== 422 || typeof code !== 'string' || code === '') {
      return throwError(err);
    }
    this.refusal = { lead, message: refusalMessage(err, `The server refused it (${code}).`) };
    return EMPTY;
  }

  private getOutgoingPayments(invoiceId: number): Observable<OutgoingPayment[]> {
    return this.outgoingPaymentsService.listOutgoingPayments({ id: invoiceId });
  }
}
